import type { ConditionOperator } from '@ricoh/ria.client.core/constants/core/data.type';
import type {
  PatternCompositeFilterDescriptor,
  PatternFilterDescriptor,
  PatternFormField,
  PatternFormFieldItem,
  PatternFormFieldItemConstant,
} from '@ricoh/ria.client.core/types/component/pattern.type';
import type { Nullable } from '@ricoh/ria.client.core/types/core/data.type';
import type { Func } from '@ricoh/ria.client.core/types/core/function.type';

import { createLogger } from '@ricoh/ria.client.core/plugins/app/logger';
import { isPatternCompositeFilterDescriptor } from '@ricoh/ria.client.core/utils/component/form';
import { getStringValue, isNotNull, isType } from '@ricoh/ria.client.core/utils/data/object';

import type { PatternAccessScope } from '~/common/logic/feature/settings/batch-type-library/pattern';
import type { Pattern } from '~/common/model/api/batch-type/pattern';
import type { ProcessFlowStepConditionRule } from '~/common/model/api/batch-type/process-flow';
import type { DataType } from '~/common/model/api/common';

import { DATA_TYPE_GENERAL_NAMES } from '~/common/constants/feature/settings/batch-type-library';
import { CONDITION_OPERATORS } from '~/common/constants/feature/settings/condition';
import fieldLogic from '~/common/logic/feature/settings/batch-type-library/field';
import patternLogic from '~/common/logic/feature/settings/batch-type-library/pattern';

const logger = createLogger('logic/feature/settings/batch-type-library');

// * Transformation to text is not necessary
const IS_TRANSFORMATION_TEXT = false;

/**
 * Create Pattern from FilterField PatternFormField.
 *
 * @param {PatternFormField} formField FilterField PatternFormField.
 *
 * @returns {Pattern[]} FilterField Pattern.
 */
export function createFilterFieldPattern(formField: PatternFormField): Pattern[] {
  const scope: PatternAccessScope = 'document';

  return formField.reduce<Pattern[]>((prev, item) => {
    let pattern: Nullable<Pattern>;
    if (isType<PatternFormFieldItemConstant>(item, 'const')) {
      pattern = patternLogic.createPattern.fromConstant('text', item.value);
    } else {
      switch (item.tabKey) {
        case 'systemValue': {
          pattern = patternLogic.createPattern.fromSystemValue(item, scope, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'globalVariable': {
          pattern = patternLogic.createPattern.fromGlobalVariable(item, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'batchField': {
          pattern = patternLogic.createPattern.fromBatchField(item, scope, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'batchVariable': {
          pattern = patternLogic.createPattern.fromBatchVariable(item, scope, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'batchBusinessRule': {
          pattern = patternLogic.createPattern.fromBusinessRule(item, scope, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'documentField': {
          pattern = patternLogic.createPattern.fromDocumentField(item, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'documentVariable': {
          pattern = patternLogic.createPattern.fromDocumentVariable(item, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'documentBusinessRule': {
          pattern = patternLogic.createPattern.fromDocumentBusinessRule(item, IS_TRANSFORMATION_TEXT);
          break;
        }

        default: {
          pattern = undefined;
          break;
        }
      }
    }

    if (isNotNull(pattern)) {
      prev.push(pattern);
    } else {
      logger.writeWithData('warn', 'Invalid pattern item', { cause: item });
    }

    return prev;
  }, []);
}

/**
 * Create FilterField PatternFormField from Pattern.
 *
 * @param {Pattern[]} filterFieldPattern FilterField Pattern.
 *
 * @returns {PatternFormField} FilterField PatternFormField.
 */
export function createFilterFieldPatternFormField(filterFieldPattern: Pattern[]): PatternFormField {
  return filterFieldPattern.reduce<PatternFormField>((prev, pattern) => {
    let item: Nullable<PatternFormFieldItem>;
    switch (pattern.$is) {
      case 'const': {
        item = patternLogic.createPatternFormFieldItem.fromPatternConstant(pattern);
        break;
      }

      case 'now':
      case 'guid':
      case 'random': {
        item = patternLogic.createPatternFormFieldItem.fromPatternGenerate(pattern, IS_TRANSFORMATION_TEXT);
        break;
      }

      case 'global': {
        item = patternLogic.createPatternFormFieldItem.fromPatternGlobal(pattern);
        break;
      }

      case 'batch': {
        item = patternLogic.createPatternFormFieldItem.fromPatternBatch(pattern);
        break;
      }

      case 'document': {
        item = patternLogic.createPatternFormFieldItem.fromPatternDocument(pattern);
        break;
      }

      default: {
        item = undefined;
        break;
      }
    }

    if (isNotNull(item)) {
      prev.push(item);
    } else {
      logger.writeWithData('warn', 'Invalid pattern', { cause: pattern });
    }

    return prev;
  }, []);
}

/**
 * Create ProcessFlowConditionRule FormField.
 *
 * @param {ProcessFlowStepConditionRule} conditionRule ProcessFlowConditionRule.
 *
 * @returns {PatternCompositeFilterDescriptor} ProcessFlowConditionRule FormField.
 */
export function createProcessFlowConditionRuleFormField(conditionRule: ProcessFlowStepConditionRule): PatternCompositeFilterDescriptor {
  const filter = (function build(source: ProcessFlowStepConditionRule): PatternFilterDescriptor | PatternCompositeFilterDescriptor {
    if ('group' in source) {
      return {
        logic: source.group,
        filters: source.rules?.map(build) ?? [],
      };
    }

    const { operator, value } = source;
    return {
      field: createFilterFieldPatternFormField(source.field),
      operator,
      value: fieldLogic.createDataValueFormField(value) ?? null,
    };
  })(conditionRule);

  if (isPatternCompositeFilterDescriptor(filter)) {
    return filter;
  }

  return {
    logic: 'and',
    filters: [filter],
  };
}

/**
 * Create ProcessFlowConditionRule from ProcessFlowConditionRule FormField.
 *
 * @param {PatternCompositeFilterDescriptor} conditionRuleFormField ProcessFlowConditionRule FormField.
 * @param {Func<[field: Nullable<PatternFormField>], Nullable<DataType>>} getFilterDataType FilterField DataType getter.
 *
 * @returns {ProcessFlowStepConditionRule} ProcessFlowConditionRule.
 */
export function createProcessFlowConditionRule(
  conditionRuleFormField: PatternCompositeFilterDescriptor,
  getFilterDataType: Func<[field: Nullable<PatternFormField>], Nullable<DataType>>,
): ProcessFlowStepConditionRule {
  return (
    (function build(descriptor: PatternFilterDescriptor | PatternCompositeFilterDescriptor): Nullable<ProcessFlowStepConditionRule> {
      if (isPatternCompositeFilterDescriptor(descriptor)) {
        return {
          group: descriptor.logic,
          rules: descriptor.filters.map((filter) => build(filter)).filter<ProcessFlowStepConditionRule>((filter) => !!filter),
        };
      }

      const type = getFilterDataType(descriptor.field);
      if (!type || type.name === 'table') {
        logger.writeWithData('warn', 'Invalid type', { descriptor, type });
        return undefined;
      }

      const typeName = DATA_TYPE_GENERAL_NAMES.includes(type.name) ? type.name : 'list';
      const operator = getStringValue<ConditionOperator>(descriptor.operator);
      if (!CONDITION_OPERATORS[typeName].includes(operator)) {
        logger.writeWithData('warn', 'Invalid operator', descriptor);
        return undefined;
      }

      return {
        field: createFilterFieldPattern(descriptor.field ?? []),
        operator: getStringValue(descriptor.operator),
        value: fieldLogic.createDataValue(type, descriptor.value),
      };
    })(conditionRuleFormField) ?? {
      group: 'and',
      rules: [],
    }
  );
}
